import { MarkerType } from "@xyflow/react";
import { get, keyBy } from "lodash";
import {
  describeRules,
  EXIT_TYPES,
  exitType,
  RULE_MODES,
  splitDelay,
} from "./next-steps";
import { isRoutingEdge } from "./graph";

export const EDGE_TONES = {
  RULE: "rule",
  TIMER: "timer",
  EVENT: "event",
  BACK: "back",
};

const MARKER_COLORS = {
  [EDGE_TONES.RULE]: "#8a94a8",
  [EDGE_TONES.TIMER]: "#b58a1b",
  [EDGE_TONES.EVENT]: "#0a72b5",
  [EDGE_TONES.BACK]: "#9aa3b5",
};

const TONE_BY_EXIT = {
  [EXIT_TYPES.TIMER]: EDGE_TONES.TIMER,
  [EXIT_TYPES.EVENT]: EDGE_TONES.EVENT,
  [EXIT_TYPES.BACK]: EDGE_TONES.BACK,
};

const rulePill = (edge, edges, variableByAccessor, labels) => {
  const rules = describeRules(edges, edge.source);
  const rule = rules.find((item) => item.edge.id === edge.id);
  if (!rule) return null;
  const rank = rules.length > 1 ? rule.rank : null;
  if (rule.mode === RULE_MODES.OTHERWISE)
    return { tone: EDGE_TONES.RULE, rank, text: labels.OTHERWISE };
  if (rule.mode === RULE_MODES.ALWAYS)
    return rank ? { tone: EDGE_TONES.RULE, rank, text: labels.ALWAYS } : null;
  const conditions = get(edge, "data.conditions") || [];
  const accessor = get(conditions, "0.leftOperand.accessor");
  const field =
    get(variableByAccessor, [accessor, "label"]) || accessor || labels.NO_FIELD;
  const more = conditions.length > 1 ? ` +${conditions.length - 1}` : "";
  return {
    tone: EDGE_TONES.RULE,
    rank,
    text: `${labels.IF} ${field.toLowerCase()}${more}`,
  };
};

const exitPill = (edge, labels) => {
  const type = exitType(edge);
  if (type === EXIT_TYPES.BACK) return null;
  if (type === EXIT_TYPES.EVENT)
    return {
      tone: EDGE_TONES.EVENT,
      rank: null,
      text: get(edge, "data.event") || "?",
    };
  const delay = splitDelay(get(edge, "data.delaySeconds"));
  return {
    tone: EDGE_TONES.TIMER,
    rank: null,
    text: labels.after(`${delay.value} ${labels.SHORT_UNITS[delay.unit]}`),
  };
};

export const hasPill = (edge, edges = []) => {
  if (!isRoutingEdge(edge)) return exitType(edge) !== EXIT_TYPES.BACK;
  const rules = describeRules(edges, edge.source);
  const rule = rules.find((item) => item.edge.id === edge.id);
  if (!rule) return false;
  return rule.mode !== RULE_MODES.ALWAYS || rules.length > 1;
};

export const decorateEdges = (edges = [], variables = [], labels) => {
  const variableByAccessor = keyBy(variables, "accessor");
  return edges.map((edge) => {
    const routing = isRoutingEdge(edge);
    const tone = routing ? EDGE_TONES.RULE : TONE_BY_EXIT[exitType(edge)];
    return {
      ...edge,
      type: "rule",
      className: `canvas-edge--${tone}`,
      markerEnd: {
        type: MarkerType.Arrow,
        width: 16,
        height: 16,
        strokeWidth: 1.3,
        color: MARKER_COLORS[tone],
      },
      data: {
        ...edge.data,
        pill: routing
          ? rulePill(edge, edges, variableByAccessor, labels)
          : exitPill(edge, labels),
      },
    };
  });
};
